import { create } from 'zustand';

import type {
  HistoryResponse,
  MemorySummary,
  ReadingRecord,
  SettingsResponse,
  UserPreferences,
  UserSession,
} from '@lilith-arcana-os/domain';

interface UserState {
  session: UserSession;
  preferences: UserPreferences;
  history: ReadingRecord[];
  memorySummary: MemorySummary;
  setSession: (session: UserSession) => void;
  setPreferences: (preferences: UserPreferences) => void;
  setHistory: (history: HistoryResponse['items']) => void;
  setMemorySummary: (summary: MemorySummary) => void;
  hydrateFromSettings: (settings: SettingsResponse) => void;
}

const initialPreferences: UserPreferences = {
  lilith_mode: true,
  kabbalistic_overlay: false,
  qliphotic_readings: false,
  show_decan_info: true,
  show_angelic_info: false,
  show_dignity_info: true,
  show_temporal_info: true,
  llm_local: null,
  llm_remote: null,
  preferred_backend: 'auto',
  entropy_preference: 'auto',
  language: 'it',
  ritual_mode: true,
  animation_speed: 'normal',
  show_audit_trail: true,
  auto_save_readings: true,
  significator_card_id: null,
  birth_decan: null,
  natal_element: null,
};

const initialMemorySummary: MemorySummary = {
  total_readings: 0,
  shannon_entropy: 0,
  entropy_interpretation: 'Memory field not initialized yet.',
  dominant_cards: [],
  dominant_suits: [],
  dominant_elements: [],
  dominant_sephiroth: [],
  recurring_pairs: [],
  modality_balance: {
    Cardinale: 0,
    Fisso: 0,
    Mutabile: 0,
  },
  themes: [],
  temporal_affinities: [],
};

export const useUserStore = create<UserState>((set) => ({
  session: {
    isLoggedIn: false,
    nonce: '',
  },
  preferences: initialPreferences,
  history: [],
  memorySummary: initialMemorySummary,
  setSession: (session) => set({ session }),
  setPreferences: (preferences) => set({ preferences }),
  setHistory: (history) => set({ history }),
  setMemorySummary: (memorySummary) => set({ memorySummary }),
  hydrateFromSettings: (settings) => set({ preferences: settings.preferences }),
}));
